import { Injectable } from '@angular/core';
import {
    SearchSymbolResults, SearchSymbolRequest, SearchSymbolResponse, AddSpecificSymbolParams,
    AddSpecificSymbolResponse,
    FOBanSymbolResponse
} from 'sharedweb/app.interface';
import { FilterOptions, IndexSymbol, OPTION_TYPE, SEARCH_SYMBOL_INSTRUMENT_TYPE, SearchTree, SNode, symbolDateFormat } from '../../../sharedweb/components/add-symbol/symbol-search';
import { SymbolLoadService } from '../symbol-load/symbol-load.service';
import { MAX_SEARCH_RESULTS } from '../../helpers/shared-constants';
import { SharedUtils } from 'sharedweb/helpers/shared.utilities';
import { OrderCategory, Series } from 'sharedweb/app.structs';
import { applicationUrls } from 'sharedweb/helpers/urls';
import { HttpClient } from '@angular/common/http';
import { BSE_OC_ALLOWED_INDICES, INDEXSYMBOL, MOMENT_OPTION_FORMAT, Utils } from 'src/app/_helpers';
import { ConfigurationKeys, FOMarketMap, MarketKeywords } from 'src/app/app.structs';
import * as moment from 'moment';
import { OptionType } from '../utProto/enums_pb';
import { ConfigurationService } from 'src/app/_shared/providers/configuration/configuration.service';


declare const resultant;

declare const symbol_info;

declare const indices_cp_info;

/**
 * DONE FOR `RT-8446`
 * ===========
 * ISSUE AND FIX
 * When ever symbol search is used used in top level module
 * (trading, sidebar, profile) a separate SymbolSearch service is used in each top level
 * module, so if user open any component using from sidebar (let's say SMW/ Batch Order)
 * There was a delay observed as side bar module will first create new symbol search service
 * hence tries will be reconstructed (which is a heavy operation).
 * Therefore keeping tries in singleton.
 */

enum InstrumentTypeEnum {
    FUT = 'fut',
    OPT = 'opt',
    INDEX = 'index'
}
export class TrieClass {
    private static instance = new TrieClass();
    loaded = false;
    searchedSymbols: SearchSymbolResults[];
    symbolTrie: SearchTree = new SearchTree();
    companyNameTrie: SearchTree = new SearchTree();
    marketTrie: SearchTree = new SearchTree();
    priceTrie: SearchTree = new SearchTree();
    dateTrie: SearchTree = new SearchTree();
    strikeTrie: SearchTree = new SearchTree();
    optionTrie: SearchTree = new SearchTree();
    symbolSearchResultsarray: SearchSymbolResults[] = [];
    instrumentTrie: SearchTree = new SearchTree();

    spreadList: Set<string | number> = new Set();
    scripMaster: Object = {};
    formattedSymbolInfo: Object = {};

    static getInstance() {
        return TrieClass.instance;
    }
}


declare global {
    interface Set<T> {
        union(firstSet: Set<T>, secondSet: Set<T>): Set<T>;
    }
}

Set.prototype.union = (firstSet: Set<any>, secondSet: Set<any>) => {
    if (!firstSet) {
        firstSet = new Set();
    }
    for (const el of Array.from(secondSet)) {
        firstSet.add(el);
    }
    return firstSet;
};

@Injectable({
    providedIn: 'root'
})
export class SymbolSearchService {
    MAX_SEARCH_RESULTS = MAX_SEARCH_RESULTS;
    /**
     * Map contains value of an array whose first value can a number or '' (empty string) representing ASM/GSM Ban
     * second value of array can be true or false containing FO Ban
     */
    symbolInfoMap = new Map<string, any[]>();
    indicesClosePriceMap = new Map<string, number>()
    foBannedSymbols: FOBanSymbolResponse;
    uniqueMarket = new Set<string>();
    ocBannedMarkets = [];

    constructor(
        private _symbolLoadService: SymbolLoadService,
        private _httpClient: HttpClient,
        private configurationService: ConfigurationService
    ) {
        if (!TrieClass.getInstance().loaded) {
            this.constructTries();
        }
    }

    getFOBannedSymbols() {
        this._httpClient.get(applicationUrls.foBannedSymbolUrl).subscribe({
            next: (res: FOBanSymbolResponse) => {
                this.foBannedSymbols = res;
            },
            error: (err) => {
                console.log(err, 'Error in FOBannedSymbols Request')
            }
        })
    }

    insertInDateTrie(dateArray: string[], symbolObject: SearchSymbolResults) {

        dateArray.forEach(date => {
            TrieClass.getInstance().dateTrie.insert(date, symbolObject);
        });

        const expiry = moment(symbolObject.expiry);

        // Format: DDMMM (e.g., 21AUG)
        const ddmmm = expiry.format('DDMMM').toUpperCase();
        TrieClass.getInstance().dateTrie.insert(ddmmm, symbolObject);

        // Format: MMMYY (e.g., AUG24)
        const mmmyy = expiry.format('MMMYY').toUpperCase();
        TrieClass.getInstance().dateTrie.insert(mmmyy, symbolObject);

        // Format: MMM (e.g., AUG)
        const mmm = expiry.format('MMM').toUpperCase();
        TrieClass.getInstance().dateTrie.insert(mmm, symbolObject);
    }

    constructTries() {

        this.ocBannedMarkets = this.configurationService.remoteConfigCacheObj[ConfigurationKeys.optionChainDisabledMarkets] || [];

        this._symbolLoadService.load('symbol_info_search').then(() => {

            Object.keys(symbol_info).forEach(key => {
                this.symbolInfoMap.set(key, symbol_info[key]);
            })
        });

        this._symbolLoadService.load('indices_info').then(() => {
            Object.keys(indices_cp_info).forEach(key => {
                this.indicesClosePriceMap.set(key, indices_cp_info[key][0]);
            })
        });
        // tslint:disable-next-line:no-console
        // You can load multiple scripts by just providing the key as argument into load method of the service
        this._symbolLoadService.load('scrip_name_search').then(data => {

            /**
             * data is filled into tries to be accessed further
             */
            Object.keys(resultant).forEach(market => {
                const marketObject = resultant[market];
                Object.keys(marketObject).forEach(symbol => {
                    this.uniqueMarket.add(market);
                    const common_stock_group = marketObject[symbol][0];
                    const future_group = marketObject[symbol][1];
                    const option_group = marketObject[symbol][2];
                    const index_group = marketObject[symbol][3];
                    const FoObject = resultant[FOMarketMap[market] ?? market];
                    let optionExistsInFO = false;
                    if (FoObject && FoObject[symbol] && FoObject[symbol][2]) {
                        optionExistsInFO = (FoObject[symbol][2] as any)?.length > 0
                    }
                    if (common_stock_group.length > 0) {
                        common_stock_group.forEach(groupName => {
                            const symbolObject: SearchSymbolResults = {
                                market_id: market,
                                security_id: groupName[1],
                                symbol,
                                series: groupName[0],
                                instrument_type: 'common_stock',
                                scrip_name: symbol + ' ' + groupName[0],
                                symbol_id: groupName[4],
                                lot_size: null,
                                company_name: groupName[2],
                                search_instrument_type: 'equity',
                                options_exists: optionExistsInFO && !this.ocBannedMarkets.includes(market)
                            };
                            // maintaining a symbolSearchResultsarray array to be used for fallback method
                            TrieClass.getInstance().symbolSearchResultsarray.push(symbolObject);
                            TrieClass.getInstance().scripMaster[market + '_' + groupName[1]] = symbolObject;
                            TrieClass.getInstance().marketTrie.insert(market, symbolObject);
                            TrieClass.getInstance().symbolTrie.insert(symbol + ' ' + groupName[0], symbolObject);
                            TrieClass.getInstance().companyNameTrie.insert(groupName[2], symbolObject);
                        });
                    }

                    // Insert Futures into SearchTree
                    if (future_group.length > 0) {
                        future_group.forEach(groupName => {
                            const symbolObject: SearchSymbolResults = {
                                market_id: market,
                                security_id: groupName[2],
                                symbol,
                                expiry: groupName[0][0],
                                instrument_type: 'future',
                                scrip_name: symbol + ' ' + groupName[0][0] + (groupName[1] !== 0 ? ' ' + groupName[1][0] : ''),
                                series: null,
                                symbol_id: groupName[4],
                                lot_size: null,
                                search_instrument_type: 'fut',
                                contract_type: OrderCategory.single,
                                underlying_id: groupName[3],
                                options_exists: option_group?.length > 0 && (!this.ocBannedMarkets.includes(market) || BSE_OC_ALLOWED_INDICES.includes(symbol)),
                            };
                            // maintaining a symbolSearchResultsarray array to be used for fallback method
                            TrieClass.getInstance().symbolSearchResultsarray.push(symbolObject);
                            TrieClass.getInstance().scripMaster[market + '_' + groupName[2]] = symbolObject;
                            TrieClass.getInstance().marketTrie.insert(market, symbolObject);
                            TrieClass.getInstance().symbolTrie.insert(symbol, symbolObject);
                            this.insertInDateTrie(groupName[0], symbolObject);
                            TrieClass.getInstance().instrumentTrie.insert('fut', symbolObject);
                            if (groupName[1]) {
                                symbolObject.contract_type = OrderCategory.spread;
                                symbolObject.expiry2 = groupName[1][0];
                                this.insertInDateTrie(groupName[1], symbolObject);
                                TrieClass.getInstance().spreadList.add(market + '_' + groupName[2]);
                            }
                        });
                    }
                    // Insert Options into SearchTree
                    if (option_group.length > 0) {
                        option_group.forEach((groupName, idx) => {
                            const symbolObject: SearchSymbolResults = {
                                market_id: market,
                                security_id: groupName[3],
                                symbol,
                                expiry: groupName[0][0],
                                strike_price: +groupName[1],
                                option_type: groupName[2],
                                instrument_type: 'option',
                                scrip_name: symbol + ' ' + groupName[0][0] + ' ' + +groupName[1] + ' ' + groupName[2],
                                series: null,
                                symbol_id: groupName[5],
                                lot_size: null,
                                search_instrument_type: 'opt',
                                underlying_id: groupName[4],
                                options_exists: (!this.ocBannedMarkets.includes(market) || BSE_OC_ALLOWED_INDICES.includes(symbol))
                            };
                            // maintaining a symbolSearchResultsarray array to be used for fallback method
                            TrieClass.getInstance().symbolSearchResultsarray.push(symbolObject);
                            TrieClass.getInstance().scripMaster[market + '_' + groupName[3]] = symbolObject;
                            TrieClass.getInstance().marketTrie.insert(market, symbolObject);
                            TrieClass.getInstance().symbolTrie.insert(symbol, symbolObject);
                            this.insertInDateTrie(groupName[0], symbolObject);
                            TrieClass.getInstance().strikeTrie.insert(groupName[1], symbolObject);
                            TrieClass.getInstance().optionTrie.insert(groupName[2], symbolObject);
                            TrieClass.getInstance().instrumentTrie.insert('opt', symbolObject);
                        });
                    }

                    if (index_group.length > 0) {
                        const symbolObject: SearchSymbolResults = {
                            market_id: market,
                            security_id: index_group[0],
                            symbol,
                            series: null,
                            instrument_type: 'index',
                            scrip_name: symbol,
                            symbol_id: null,
                            lot_size: null,
                            search_instrument_type: 'index',
                            options_exists: INDEXSYMBOL[symbol?.toUpperCase()] ? true : false
                        };
                        // maintaining a symbolSearchResultsarray array to be used for fallback method
                        TrieClass.getInstance().symbolSearchResultsarray.push(symbolObject);
                        TrieClass.getInstance().scripMaster[market + '_' + index_group[0]] = symbolObject;
                        TrieClass.getInstance().marketTrie.insert(market, symbolObject);
                        TrieClass.getInstance().symbolTrie.insert(symbol, symbolObject);
                        TrieClass.getInstance().instrumentTrie.insert('index', symbolObject);

                    }
                });
            });

            // Script Loaded Successfully
            TrieClass.getInstance().loaded = true;
        }).catch(error => console.log(error));
        // tslint:disable-next-line:no-console
    }

    getNearestFutureExpiry(currentExpiry: string, market: string, symbol: string) {
        const expiries = this.getStrikePriceExpiryList(`${market} ${symbol}`, SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE, FilterOptions.basic);
        const expectedDate = moment(currentExpiry, MOMENT_OPTION_FORMAT);
        let requiredDate = '';
        if (expiries.length) {
            expiries.every((expiry: string) => {
                const currentEle = moment(expiry, MOMENT_OPTION_FORMAT);
                if (currentEle.isSameOrAfter(expectedDate)) {
                    requiredDate = currentEle.format(MOMENT_OPTION_FORMAT).toUpperCase();
                    return false;
                }
                return true;
            })
        }
        return requiredDate;
    }

    getSymbolName(key: string) {
        return TrieClass.getInstance().scripMaster[key] && TrieClass.getInstance().scripMaster[key].symbol;
    }

    getSymbol(key: string) {
        return TrieClass.getInstance().scripMaster[key];
    }


    getFormattedSymbol(key: string) {
        // If the formatted symbol is already present, return it
        if (TrieClass.getInstance().formattedSymbolInfo && TrieClass.getInstance().formattedSymbolInfo[key]) {
            return TrieClass.getInstance().formattedSymbolInfo[key];
        }

        try {
            const symbol = this.getSymbol(key);

            if (!symbol){
                console.warn('Symbol not found for key:', key);
                return '';
            }

            if (symbol?.instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE) {
                const expiryDate = moment(symbol.expiry, symbolDateFormat.fullDate);

                if (!expiryDate.isValid()) {
                    console.error('Invalid expiry date:', symbol.expiry);
                    return symbol.symbol;
                }

                const month = expiryDate.format(symbolDateFormat.monthly).toUpperCase();
                const yearSuffix = +(expiryDate.format(symbolDateFormat.yearly));
                const date = expiryDate.format(symbolDateFormat.date);
                const currentYear = +(moment().format(symbolDateFormat.yearly));


                if (symbol.contract_type === OrderCategory.spread) {
                    const expiryDate2 = moment(symbol.expiry2, symbolDateFormat.fullDate);
                    if (!expiryDate2.isValid()) {
                        return symbol.symbol;
                    }

                    const month2 = expiryDate2.format(symbolDateFormat.monthly).toUpperCase();
                    const yearSuffix2 = +(expiryDate2.format(symbolDateFormat.yearly));
                    const date2 = expiryDate2.format(symbolDateFormat.date);

                    const spreadText = `${date} ${month}${yearSuffix > currentYear ? yearSuffix.toString().slice(-2) : ''} ${date2} ${month2}${yearSuffix2 > currentYear ? yearSuffix2.toString().slice(-2) : ''} SPD`;
                    TrieClass.getInstance().formattedSymbolInfo[key] = `${symbol.symbol} ${spreadText}`;
                    return TrieClass.getInstance().formattedSymbolInfo[key];
                }


                const yearText = yearSuffix > currentYear ? yearSuffix.toString().slice(-2) : '';

                // Format the future text  
                const futureText = `${date} ${month}${yearText} FUT`

                TrieClass.getInstance().formattedSymbolInfo[key] = `${symbol.symbol} ${futureText}`;
                return TrieClass.getInstance().formattedSymbolInfo[key];
            }
            else if (symbol?.instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION) {
                // Extract expiry details
                const expiryDate = moment(symbol.expiry, symbolDateFormat.fullDate);
                const month = moment(expiryDate).format(symbolDateFormat.monthly).toUpperCase();
                const date = moment(expiryDate).format(symbolDateFormat.date);
                const year = +(expiryDate.format(symbolDateFormat.yearly));
                const currentYear = +(moment().format(symbolDateFormat.yearly));

                // Format the expiry text
                const expiryText = `${date} ${month}${year > currentYear ? year.toString().slice(-2) : ''}`


                TrieClass.getInstance().formattedSymbolInfo[key] = `${symbol.symbol} ${expiryText} ${symbol.strike_price} ${symbol.option_type}`;
                return TrieClass.getInstance().formattedSymbolInfo[key];
            }
            else {
                if ((symbol?.series) && (symbol?.series !== Series.EQ && symbol?.series !== Series.A)) {
                    TrieClass.getInstance().formattedSymbolInfo[key] = symbol.scrip_name;
                    return TrieClass.getInstance().formattedSymbolInfo[key];
                }

                TrieClass.getInstance().formattedSymbolInfo[key] = symbol?.symbol;
                return TrieClass.getInstance().formattedSymbolInfo[key];
            }
        }
        catch (error) {
            console.error("error", error);
            return '';
        }
    }

    advanceSearch(value: string, instrument_type: SEARCH_SYMBOL_INSTRUMENT_TYPE, key = FilterOptions.empty, option_type: OPTION_TYPE | string = OPTION_TYPE.call, expiry?: string, strike_price?: string, returnEntireData = false) {
        /**
         * calling search function only user has changed the serach keyword
         */
        let searchedSymbols = this._search(value, false, null, false, true);
        if (searchedSymbols && searchedSymbols.length) {
            switch (key) {
                case FilterOptions.expiry:
                    if (instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE) {
                        searchedSymbols = searchedSymbols.filter(el => (el.instrument_type === instrument_type && el.contract_type === OrderCategory.single && (`${el.market_id} ${el.symbol}` === value)));
                    }
                    else {
                        searchedSymbols = searchedSymbols.filter(el => el.instrument_type === instrument_type
                            && el.option_type === option_type && (`${el.market_id} ${el.symbol}` === value));
                    }
                    searchedSymbols = searchedSymbols.sort(Utils.sortExpiry);
                    break;
                case FilterOptions.strike_price:
                    searchedSymbols = searchedSymbols.filter(el => el.instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION
                        && el.option_type === option_type && el.expiry === expiry && (`${el.market_id} ${el.symbol}` === value));
                    break;
                // Returns single symbol in array on the basis of static data.
                case FilterOptions.symbol:
                    if (instrument_type === SEARCH_SYMBOL_INSTRUMENT_TYPE.FUTURE) {
                        searchedSymbols = searchedSymbols.filter(el => el.instrument_type === instrument_type && el.expiry == expiry && (`${el.market_id} ${el.symbol}` === value));
                    }
                    else {
                        searchedSymbols = searchedSymbols.filter(el => el.instrument_type === instrument_type
                            && (!option_type || el.option_type === option_type) && (!expiry || el.expiry === expiry) && (!strike_price || el.strike_price.toString() === strike_price) && (`${el.market_id} ${el.symbol}` === value));
                    }
                    break;

                case FilterOptions.index:
                    /**
                     * filtering seached result based on security id 
                     * so that exact result can be passed where ever needed
                     */
                    value = value.substring(value.indexOf(' '), value.length).trim();
                    searchedSymbols = searchedSymbols.filter(el => el.security_id === INDEXSYMBOL[value]);
            }
        }
        return searchedSymbols;
    }

    /**
     * function used for getting strike price list or expiry list
     * based on the arguments passed to the function
     * value is the combination of exchangeId + symbol
     */
    getStrikePriceExpiryList(value: string, instrument_type: SEARCH_SYMBOL_INSTRUMENT_TYPE, key?: FilterOptions) {
        let searchResult = this.advanceSearch(value, instrument_type, FilterOptions.expiry);


        /**
         * filterOptions.basic: if this option is selected by user, returning expiry list firstly
         * later based on the strikePriceOffset reurn expected strike price result.
         */
        switch (key) {
            case FilterOptions.basic:
                if (searchResult && searchResult.length) {
                    return searchResult.map(r => r.expiry).filter((v, i, a) => a.indexOf(v) === i);
                }
                return [];
        }
        return [];
    }

    _search(value: string, searchSymbolFiltered: boolean, marketFilter?: string[], hideIndices = false, returnEntireData = false, isSipSearch = false) {
        /**
         * Scrip Name Search
         */
        TrieClass.getInstance().searchedSymbols = [];
        value = value.toUpperCase().trim();
        const searchValues = value.toUpperCase().split(' ');
        let symbolsSearched = false;
        let results = new Set();
        let isMarketFound = false;
        let marketKeyword;
        // Commenting for now.
        //if serachvalue includes only one keyword and it is bse or mcx then directly set isMarketFound to true
        // if ((searchValues[0] === 'BSE' || searchValues[0] === 'MCX')) {
        //     isMarketFound = true;
        // }
        
        for (let i = 0; i < searchValues.length; i++) {
            const keyWord = searchValues[i];
            if (keyWord !== '') {
                const symbolKeyword = TrieClass.getInstance().symbolTrie.search(keyWord);
                // Holds results from symbol search that are used later
                let symResults;
                if (symbolKeyword.node != null) {
                    const symbolResult = new Set(TrieClass.getInstance().symbolTrie.getWords(symbolKeyword.node, symbolKeyword.retString));
                    if (!results.size) { results = symbolResult; }
                    symResults = new Set(Array.from(results).filter(x => symbolResult.has(<string | number>x)));
                    // Will not be searching in symbol trie once it is searched as it will block option type search (as most
                    // strings will be found in this trie)
                    symbolsSearched = true;
                    if (symResults.size) {
                        results = symResults;
                    }
                }
                if (!symbolsSearched && !isMarketFound) {
                    marketKeyword = TrieClass.getInstance().marketTrie.search(keyWord);
                    if (!searchSymbolFiltered) {
                        if (marketKeyword.node != null) {
                            isMarketFound = true;
                            const marketResult = new Set(TrieClass.getInstance().marketTrie.getWords(
                                marketKeyword.node, marketKeyword.retString));
                            // If no result is returned from intersection, then let previous results be shown as result or used
                            // for comparison os next search string. This has been done for all tries.
                            if (!results.size) { results = marketResult; }
                            results = new Set(Array.from(results).filter(x => marketResult.has(<string | number>x)));
                            continue;
                        }
                    }
                }

                if (InstrumentTypeEnum[keyWord]) {
                    const instrumentKeyword = TrieClass.getInstance().instrumentTrie.search(keyWord);
                    if (instrumentKeyword.node != null) {
                        const instrumentResult = new Set(TrieClass.getInstance().instrumentTrie.getWords(
                            instrumentKeyword.node, instrumentKeyword.retString));
                        if (!results.size) { results = instrumentResult; }
                        if (symResults && symResults.size) {
                            results = results.union(symResults, instrumentResult);
                        } else {
                            results = new Set(Array.from(results).filter(x => instrumentResult.has(<string | number>x)));
                        }
                        continue;
                    }
                }

                const optionKeyword = TrieClass.getInstance().optionTrie.search(keyWord);
                if (optionKeyword.node != null) {
                    const optionResult = new Set(TrieClass.getInstance().optionTrie.getWords(optionKeyword.node, optionKeyword.retString));
                    if (!results.size) { results = optionResult; }
                    results = new Set(Array.from(results).filter(x => optionResult.has(<string | number>x)));
                    results = results.union(symResults, results);
                    continue;
                }

                const dateKeyword = TrieClass.getInstance().dateTrie.search(keyWord);
                if (dateKeyword.node != null) {
                    const dateResult = new Set(TrieClass.getInstance().dateTrie.getWords(dateKeyword.node, dateKeyword.retString));
                    if (!results.size) { results = dateResult; }
                    const intersection = new Set(Array.from(results).filter(x => dateResult.has(<string | number>x)));
                    if (intersection.size) {
                        results = intersection;
                    } else {
                        results = results.union(results, dateResult);
                    }
                    results = results.union(symResults, results);
                    continue;
                }

                const strikeKeyword = TrieClass.getInstance().strikeTrie.search(keyWord);
                if (strikeKeyword.node != null) {
                    const strikeResult = new Set(TrieClass.getInstance().strikeTrie.getWords(strikeKeyword.node, strikeKeyword.retString));
                    if (!results.size) { results = strikeResult; }
                    results = new Set(Array.from(results).filter(x => strikeResult.has(<string | number>x)));
                    results = results.union(symResults, results);
                    continue;

                }

                const companyNameKeyword = TrieClass.getInstance().companyNameTrie.search(keyWord);
                if (companyNameKeyword.node != null) {
                    const companyNameResult = new Set(TrieClass.getInstance().companyNameTrie.getWords(companyNameKeyword.node,
                        companyNameKeyword.retString));
                    if (!results.size) { results = companyNameResult; }
                    const companyIntersection = new Set(Array.from(results).filter(x => companyNameResult.has(<string | number>x)));
                    results = companyIntersection.size ? companyIntersection : results;
                    results = results.union(symResults, results);
                    if (results.size) {
                        continue;
                    }
                }
            }
        }
        const symbolsArray: SearchSymbolResults[] = Array.from(results).map((x: any) => TrieClass.getInstance().scripMaster[x]);
        TrieClass.getInstance().searchedSymbols = symbolsArray.
            filter(s => !marketFilter ? true : marketFilter.includes(s.market_id)).
            filter(s => hideIndices ? s.instrument_type !== 'index' : true);
        if (TrieClass.getInstance().searchedSymbols.length < MAX_SEARCH_RESULTS) {
            for (let i = 0; i < TrieClass.getInstance().symbolSearchResultsarray.length; i++) {
                if (TrieClass.getInstance().symbolSearchResultsarray[i].scrip_name.includes(value) ||
                    (TrieClass.getInstance().symbolSearchResultsarray[i].company_name &&
                        TrieClass.getInstance().symbolSearchResultsarray[i].company_name.includes(value))) {
                    TrieClass.getInstance().searchedSymbols.push(TrieClass.getInstance().symbolSearchResultsarray[i]);
                }
                if (TrieClass.getInstance().searchedSymbols.length >= MAX_SEARCH_RESULTS) {
                    break;
                }
            }
            TrieClass.getInstance().searchedSymbols = TrieClass.getInstance().searchedSymbols
                .filter((item, index) => TrieClass.getInstance().searchedSymbols
                    .indexOf(item) === index).
                // If marketFilter is null, Allow all symbols. If not null check if only symbols with
                // markets in marketFilter are allowed.
                filter(s => !marketFilter ? true : marketFilter.includes(s.market_id)).
                filter(s => hideIndices ? s.instrument_type !== 'index' : true);
        }
        
        if(isSipSearch){
            TrieClass.getInstance().searchedSymbols = TrieClass.getInstance().searchedSymbols.filter(s => s.instrument_type ==="common_stock"); 
        }

        if(isSipSearch){
            TrieClass.getInstance().searchedSymbols = TrieClass.getInstance().searchedSymbols.filter(s => s.instrument_type ==="common_stock"); 
        }

        if (returnEntireData) {
            return TrieClass.getInstance().searchedSymbols.sort(SharedUtils.customSortForSymbolSearch());
        }

        return TrieClass.getInstance().searchedSymbols.sort(SharedUtils.customSortForSymbolSearch()).splice(0, MAX_SEARCH_RESULTS);
    }


    /**
     * Compare two scrip names for sorting the array of objects.
     */
    // modifiedSort(obj1, obj2) {

    //     const date1 = obj1.expiry;
    //     const date2 = obj2.expiry;

    //     const price1 = obj1.strike_price ? obj1.strike_price : '';
    //     const price2 = obj2.strike_price ? obj1.strike_price : '';

    //     if (!obj1.orderIndex) {
    //         obj1.orderIndex = InstrumentIndexEnum[obj1.instrument_type];
    //         if (obj1.instrument_type !== 'common_stock') {
    //             obj1.orderIndex = obj1.orderIndex + Date.parse(date1);
    //         }
    //         if (obj1.instrument_type === 'option') {
    //             obj1.orderIndex = obj1.orderIndex + price1.split('.')[0];
    //             if (price1.split('.').length === 2) {
    //                 obj1.orderIndex = obj1.orderIndex + price1.split('.')[1];
    //             }
    //         }
    //     }

    //     if (!obj2.orderIndex) {
    //         obj2.orderIndex = InstrumentIndexEnum[obj2.instrument_type];
    //         if (obj2.instrument_type !== 'common_stock') {
    //             obj2.orderIndex = obj2.orderIndex + Date.parse(date2);
    //         }
    //         if (obj2.instrument_type === 'option') {
    //             obj2.orderIndex = obj2.orderIndex + price2.split('.')[0];
    //             if (price1.split('.').length === 2) {
    //                 obj2.orderIndex = obj2.orderIndex + price2.split('.')[1];
    //             }
    //         }
    //     }

    //     let comparison = 0;
    //     if (obj1.orderIndex > obj2.orderIndex) {
    //         comparison = 1;
    //     } else if (obj1.orderIndex < obj2.orderIndex) {
    //         comparison = -1;
    //     }
    //     return comparison;


    // }



    _searchSymbols(value: string[], marketKeyword: string, marketNode: SNode, instrument: string = SEARCH_SYMBOL_INSTRUMENT_TYPE.OPTION, contractType: string = OrderCategory.single,
        displayField = 'symbol') {
        /**
        This function iterates through filtered set of scrip names and matches pattern based on REGEX.Common function for simple as well as
        advanced search.
        */
        let noSymbolFound = false;
        let filteredSymbols = [];
        const marketSymbols = new Set(TrieClass.getInstance().marketTrie.getWords(marketNode, marketKeyword));
        let symbols = new Set();
        for (let i = 0; i < value.length; i++) {

            const symbolNode = TrieClass.getInstance().symbolTrie.search(value[i]);
            if (symbolNode.node) {
                const symbolResult = new Set(TrieClass.getInstance().symbolTrie.getWords(symbolNode.node, symbolNode.retString));
                symbols = new Set(Array.from(marketSymbols).filter(x => symbolResult.has(x)));
            }
        }

        if (symbols.size) {
            if (contractType === OrderCategory.spread) {
                symbols = new Set(Array.from(symbols).filter((x: any) => TrieClass.getInstance().spreadList.has(x)));
            } else if (contractType === OrderCategory.single) {
                symbols = new Set(Array.from(symbols).filter((x: any) => !TrieClass.getInstance().spreadList.has(x)));
            }
            let symbolsArray = Array.from(symbols).map((x: any) => TrieClass.getInstance().scripMaster[x]);
            symbolsArray = symbolsArray.filter((x: any) => (!instrument || x.instrument_type === instrument));

            if (displayField) {
                symbolsArray = SharedUtils.getUnique(symbolsArray, displayField);
            }

            symbolsArray = symbolsArray.splice(0, MAX_SEARCH_RESULTS);

            TrieClass.getInstance().searchedSymbols = symbolsArray;
            filteredSymbols = symbolsArray;
        } else {
            noSymbolFound = true;
        }

        return [filteredSymbols, noSymbolFound];
    }

    getIndicesFromMarketID(marketKeyword: string, marketNode: SNode, instrument: string) {
        const marketSymbols = new Set(TrieClass.getInstance().marketTrie.getWords(marketNode, marketKeyword));
        let symbolsArray = Array.from(marketSymbols).map((x: any) => TrieClass.getInstance().scripMaster[x]);

        symbolsArray = symbolsArray.filter((x: any) => x.instrument_type === instrument);

        return symbolsArray;

    }

    /**
     * 
     * @param key key to get symbol Info (exchangeId + securityId of the symbol)
     * will return an array containing two values first will reflect asm_gsm Ban and second one is FO ban
     * asm_gsm Ban with values of 0, '', or 100 will not be considered as ban
     * FO ban can be 0 or 1 stating not ban or ban.
     */
    symbolInfo(key: string): any {
        return this.symbolInfoMap.get(key);
    }

    indicesInfo(key: string): any {
        return this.indicesClosePriceMap.get(key);
    }

    checkFOBan(exchangeId: number, symbol: string): boolean {
        return this.foBannedSymbols[exchangeId] && this.foBannedSymbols[exchangeId].includes(symbol);
    }

    updateFOBannedSymbols(bannedSymbols: FOBanSymbolResponse) {
        this.foBannedSymbols = bannedSymbols;
    }

    getSymbolSearchData(symbolSearchRequest: SearchSymbolRequest) {
        const url = applicationUrls.searchSymbolUrl;
        return this._httpClient.get<SearchSymbolResponse>(url, {
            params: <any>symbolSearchRequest
        });
    }

    getSpecificSymbolSearchData(specificSymbolSearchParams: AddSpecificSymbolParams) {
        const url = applicationUrls.searchSpecificSymbolUrl;
        return this._httpClient.get<AddSpecificSymbolResponse>(url, {
            params: <any>specificSymbolSearchParams
        });
    }
}
